import { useMemo } from 'react';
import moment from 'moment';
import { i18n } from '@investigator/shared/localization';
import { treatEscalationName } from '@investigator/shared/utils';
import { buildExcludedEntitiesQuery } from '@investigator/features/entities/utils';
import {
  useQueryAlertMetadataExcludedEntitiesPaginated,
  type IQueryAlertMetadataExcludedEntitiesPaginated,
} from '../queries';
import type {
  Detection,
  AlertEntity,
} from '@investigator/shared/generated/graphql';
import type { SantizedDetection } from '../pages/Detections';
import type { ApolloError } from '@apollo/client';

const { suppressedMsg } = i18n.pages.entityDetails.excludeEntity;

interface UseDetectionEntityStatusBannersParams {
  /** The detection data */
  detection?: Detection | SantizedDetection;
  /** The source entity for suppression queries */
  sourceEntity?: AlertEntity | null;
}

interface UseDetectionEntityStatusBannersResult {
  /** Array of formatted status messages for suppression and escalation banners */
  statusStrings: string[];
  /** Raw query data containing excluded entities information for action buttons */
  excludedEntitiesData?: IQueryAlertMetadataExcludedEntitiesPaginated;
  /** Loading state of the excluded entities query */
  excludedEntitiesLoading: boolean;
  /** Error state if the excluded entities query fails */
  excludedEntitiesError?: ApolloError;
}

/**
 * Custom hook to build status banner strings for detection entity suppression and escalation.
 *
 * This hook consolidates the logic for:
 * - Querying excluded entities (suppression status) from the API
 * - Building localized suppression messages when an entity is suppressed
 * - Building localized escalation messages when a detection is escalated
 * - Handling loading and error states for the suppression query
 *
 * The hook is used by both DetectionDetailsHeader and DetectionDrawerActions to ensure
 * consistent banner messages across the application.
 *
 * **Query Behavior:**
 * - The query is skipped when required data is missing (tenant_id, content_id, or sourceEntity)
 * - Returns empty status strings during loading or when no suppression/escalation exists
 * - Gracefully handles query errors by returning empty strings
 *
 * **Status Strings Order:**
 * 1. Suppression message (if entity is suppressed)
 * 2. Escalation message (if detection is escalated)
 *
 * @param params - Detection and source entity information
 * @returns Object containing status strings, query data, loading state, and error state
 *
 */
export const useDetectionEntityStatusBanners = ({
  detection,
  sourceEntity,
}: UseDetectionEntityStatusBannersParams): UseDetectionEntityStatusBannersResult => {
  // Query for excluded entities to show suppression banner
  const excludedEntitiesQueryVariables = useMemo(
    () =>
      sourceEntity && detection?.tenant_info?.tenant_id
        ? buildExcludedEntitiesQuery({
            entityName: sourceEntity.entity_name || '',
            entityType: sourceEntity.entity_type || '',
            tenantId: detection.tenant_info.tenant_id,
            contentId: detection?.alert_info?.content_id || undefined,
          })
        : undefined,
    [
      sourceEntity,
      detection?.tenant_info?.tenant_id,
      detection?.alert_info?.content_id,
    ],
  );

  const {
    data: excludedEntitiesData,
    loading: excludedEntitiesLoading,
    error: excludedEntitiesError,
  } = useQueryAlertMetadataExcludedEntitiesPaginated({
    skip:
      !detection?.tenant_info?.tenant_id ||
      !detection?.alert_info?.content_id ||
      !sourceEntity,
    variables: excludedEntitiesQueryVariables,
  });

  const excludedEntities =
    excludedEntitiesData?.queryAlertMetadataExcludedEntitiesPaginated?.excluded_entities?.filter(
      (entity): entity is NonNullable<typeof entity> => entity !== null,
    );

  // Build status strings for banner
  const statusStrings: string[] = useMemo(() => {
    // Early return if there are no status messages to display
    if (
      !excludedEntities?.length &&
      !(
        detection?.escalation_data?.escalation_status &&
        detection?.escalation_data?.escalated_timestamp &&
        treatEscalationName(
          detection?.escalation_data?.escalation_service_name ?? undefined,
        )
      )
    ) {
      return [];
    }

    const strings: string[] = [];

    // Add suppression message
    if (excludedEntities && excludedEntities.length > 0) {
      strings.push(
        i18n.formatString(
          suppressedMsg,
          sourceEntity?.entity_name ?? '',
          excludedEntities[0].created_by_alias ?? '',
          moment((excludedEntities[0].created_timestamp ?? 0) * 1000).format(
            'lll',
          ),
        ) as string,
      );
    }

    // Add escalation message if present
    const treatedEscalationName = treatEscalationName(
      detection?.escalation_data?.escalation_service_name ?? undefined,
    );
    if (
      detection?.escalation_data?.escalation_status &&
      detection?.escalation_data?.escalated_timestamp &&
      treatedEscalationName
    ) {
      const escalatedMsg = i18n.formatString(
        i18n.pages.detections.detection.sentToMsg,
        treatedEscalationName,
        detection.escalation_data.escalated_by_user_alias ||
          i18n.pages.detections.detection.unknownUser,
        moment(detection.escalation_data.escalated_timestamp * 1000).format(
          'lll',
        ),
      );
      strings.push(escalatedMsg as string);
    }

    return strings;
  }, [
    excludedEntities,
    sourceEntity?.entity_name,
    detection?.escalation_data?.escalation_service_name,
    detection?.escalation_data?.escalation_status,
    detection?.escalation_data?.escalated_timestamp,
    detection?.escalation_data?.escalated_by_user_alias,
  ]);

  return {
    statusStrings,
    excludedEntitiesData,
    excludedEntitiesLoading,
    excludedEntitiesError,
  };
};
